import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { paymentStatementAPI, type PaymentStatement } from "@/api/paymentStatement";
import { taxDeclarationAPI } from "@/api/taxDeclaration";
import { countryTaxConfigAPI } from "@/api/countryTaxConfig";
import { Icon } from "@/shared/components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { PaymentStatementView } from "./PaymentStatementView";

interface Props {
	engagementId: string | null;
	feeDisplay: string;
	/** From the engagement; local_partner means no statement will ever appear here. */
	paymentRoute: "cpi_direct" | "local_partner" | null;
	/** From the engagement; a Finance rejection is the one payment status the creator must act on. */
	paymentStatus?: string | null;
	paymentStatusReason?: string | null;
}

/**
 * The creator's payment card on the campaign page (Ticket-348). Replaces the optional invoice upload:
 * the Payment Statement appears here once the campaign manager sends the instruction to Finance.
 */
export const CreatorPaymentCard: React.FC<Props> = ({
	engagementId,
	feeDisplay,
	paymentRoute,
	paymentStatus,
	paymentStatusReason,
}) => {
	const [partnerName, setPartnerName] = useState<string | null>(null);
	useEffect(() => {
		if (paymentRoute !== "local_partner") return;
		let cancelled = false;
		// The partner is on the country configuration for the creator's declared tax country.
		taxDeclarationAPI
			.getMine()
			.then((declaration) => (declaration ? countryTaxConfigAPI.getForCountry(declaration.countryCode) : null))
			.then((config) => {
				if (!cancelled) setPartnerName(config?.partnerName ?? null);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [paymentRoute]);
	const [state, setState] = useState<{ statement: PaymentStatement | null; loading: boolean; error: string | null }>({
		statement: null,
		loading: Boolean(engagementId),
		error: null,
	});

	useEffect(() => {
		if (!engagementId) return;
		let cancelled = false;
		setState({ statement: null, loading: true, error: null });
		paymentStatementAPI
			.listMine()
			.then((rows) => {
				if (cancelled) return;
				const mine = rows.find((row) => row.campaignEngagementId === engagementId) ?? null;
				setState({ statement: mine, loading: false, error: null });
			})
			.catch((error) => {
				if (!cancelled)
					setState({
						statement: null,
						loading: false,
						error: getApiErrorMessage(error, "Unable to load your Payment Statement."),
					});
			});
		return () => {
			cancelled = true;
		};
	}, [engagementId]);

	const rejection =
		paymentStatus === "rejected_by_finance" ? (
			<RejectedNotice role="alert">
				<strong>Rejected by Finance</strong>
				<span>{paymentStatusReason || "Finance could not process this payment."}</span>
				<small>
					Check your <Link to="/account?tab=bank">bank and tax details</Link>, then let your campaign manager
					know so Finance can reopen the payment.
				</small>
			</RejectedNotice>
		) : null;

	if (state.statement)
		return (
			<>
				{rejection}
				<PaymentStatementView statement={state.statement} audience="creator" />
			</>
		);

	return (
		<Card aria-label="Payment">
			{rejection}
			<Title>My Payment Statement</Title>
			<FeeBar>
				<FeeIconWrap>
					<Icon name="financePayment" size={24} preserveOriginalColor />
				</FeeIconWrap>
				<FeeText>
					<FeeLabel>Agreed Creator Fee</FeeLabel>
					<FeeAmount>{feeDisplay}</FeeAmount>
				</FeeText>
			</FeeBar>
			{paymentRoute === "local_partner" ? (
				<Note>
					Your payment for this campaign is processed through {partnerName ?? "a local partner"}. No Payment
					Statement is issued by Creator Payments International for this route.
				</Note>
			) : state.loading ? (
				<Note>Loading…</Note>
			) : state.error ? (
				<Note role="alert">{state.error}</Note>
			) : (
				<Note>
					Your Payment Statement is issued when your campaign manager sends your approved deliverables to
					Finance. It will appear here and under <Link to="/creator/statements">Payment Statements</Link>.
				</Note>
			)}
			<Help>
				Something wrong? <Link to="/account?tab=bank">Update your bank or tax details in your profile</Link>.
				Changes apply to future statements.
			</Help>
		</Card>
	);
};

const Card = styled.section`
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 24px;
	background: #f3f5ff;
	border: 1px solid #cdd4f6;
	border-radius: 16px;
	min-width: 0;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
`;
const Title = styled.h2`
	margin: 0;
	color: ${CUSTOM_COLORS.PRIMARY};
	font:
		700 18px / normal "Poppins",
		sans-serif;
`;
const FeeBar = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	min-height: 74px;
	padding: 16px;
	border: 1px solid #e6e6e6;
	border-radius: 8px;
	background: #fff;
	box-sizing: border-box;
`;
const FeeIconWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 32px;
	background: #fafafa;
	flex: none;
`;
const FeeText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;
const FeeLabel = styled.span`
	color: #707070;
	font-size: 12px;
`;
const FeeAmount = styled.span`
	color: #303030;
	font-size: 16px;
	font-weight: 600;
`;
const Note = styled.p`
	margin: 0;
	color: #565656;
	font-size: 14px;
	line-height: 1.5;
	a {
		color: ${CUSTOM_COLORS.PRIMARY};
		font-weight: 600;
	}
`;
const Help = styled.p`
	margin: 0;
	color: #707070;
	font-size: 12px;
	line-height: 1.5;
	a {
		color: #565656;
		text-decoration: underline dotted;
	}
`;

const RejectedNotice = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 12px;
	padding: 12px 16px;
	border-radius: 12px;
	background: #fff4f4;
	border: 1px solid #ffd1d1;
	color: #303030;
	font-size: 14px;
	strong {
		color: #b3261e;
	}
	small {
		color: #565656;
		font-size: 12px;
	}
	a {
		color: #3a0ca3;
		font-weight: 600;
	}
`;
